import { useSnackbarStore } from '@/store';
import { HTTPError, TimeoutError } from 'ky';
import { z, ZodError } from 'zod';
import { i18n } from '@/config/i18n';

// Prevent duplicate error snackbars within a short interval
const RECENT_SNACKBAR_TTL_MS = 2000;
let lastSnackbar: {
  message: string;
  variant?: string;
  at: number;
} | null = null;

function shouldSuppressSnackbar(message: string, variant?: string): boolean {
  if (!lastSnackbar) return false;
  const isSameMessage = lastSnackbar.message === message;
  const isSameVariant = lastSnackbar.variant === variant;
  const withinTtl = Date.now() - lastSnackbar.at < RECENT_SNACKBAR_TTL_MS;
  return isSameMessage && isSameVariant && withinTtl;
}

export const streamingErrorSchema = z.object({
  error: z.string(),
  code: z.string(),
});

/**
 * Is thrown when mid-stream errors occur.
 * This is different from HTTP errors, which are handled by the HTTPError class of ky.
 */
export class StreamingError extends Error {
  code: string;
  constructor(error: string, code: string) {
    super(error);
    this.name = 'StreamingError';
    this.code = code;
  }
}

/**
 * Use this method to handle errors in async functions, e.g. a request
 * The method wil catch any error and parse them to a readable error message
 * Furthermore it will generate a snackbar with the readable error message
 *
 * @example
 * ```ts
 * async function fetchUser() {
 *  return handleError(async () => {
 *   const response = await fetch('https://api.example.com/user');
 *  return response.json();
 * });
 * }
 */
export async function handleError<T>(fn: () => Promise<T>): Promise<T> {
  const snackbar = useSnackbarStore();
  try {
    return await fn();
  } catch (err) {
    let message = i18n.global.t('errors.unknown');
    if (isTimeoutError(err)) {
      message = i18n.global.t('errors.timeout');
    } else if (err instanceof ZodError) {
      message = i18n.global.t('errors.parsing');
    } else if (isHTTPError(err)) {
      message = mapHttpErrorToErrorMessage(err);
    } else if (err instanceof Error) {
      message = err.message;
    }

    if (!shouldSuppressSnackbar(message, 'error')) {
      snackbar.addSnackbar({
        message,
        variant: 'error',
        soft: true,
      });
      lastSnackbar = { message, variant: 'error', at: Date.now() };
    }
    // TODO: add a logger here
    throw err instanceof Error ? err : new Error(message);
  }
}

/**
 * Maps HTTP status codes to human-readable error messages
 * @param error The HTTP error
 * @returns A user-friendly error message
 */
export function mapHttpErrorToErrorMessage(error: HTTPError): string {
  const statusCode = error.response.status;
  if (isStatusCode(statusCode)) {
    return i18n.global.t(`errors.http.${statusCode}`);
  }
  return i18n.global.t('errors.unknown_http');
}

/**
 * Enum of all HTTP status codes
 */
enum StatusCode {
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  Timeout = 408,
  Conflict = 409,
  LengthRequired = 411,
  PayloadTooLarge = 413,
  UnprocessableEntity = 422,
  TooManyRequests = 429,
  InternalServerError = 500,
  BadGateway = 502,
  ServiceUnavailable = 503,
  GatewayTimeout = 504,
}

export function isHTTPError(error: unknown): error is HTTPError {
  return error instanceof Error && error.name === 'HTTPError';
}

export function isStatusCode(code: number): code is StatusCode {
  return Object.values(StatusCode).includes(code as StatusCode);
}

export function isTimeoutError(error: unknown): error is TimeoutError {
  return error instanceof Error && error.name === 'TimeoutError';
}
